
import React, { memo, useState, useEffect } from 'react';
import { Position, useReactFlow } from '@xyflow/react';
import { NodeHandle } from '../common/NodeHandles';
import { Textarea } from '@/components/ui/textarea';

interface ConditionalNodeProps {
  data: {
    label: string;
    ifCondition?: string;
    thenAction?: string;
    otherwiseAction?: string;
  };
  id: string;
}

export const ConditionalNode = memo(({ data, id }: ConditionalNodeProps) => {
  const [ifCondition, setIfCondition] = useState(data.ifCondition || "");
  const [thenAction, setThenAction] = useState(data.thenAction || "");
  const [otherwiseAction, setOtherwiseAction] = useState(data.otherwiseAction || "");
  const { setNodes } = useReactFlow();
  
  const handleIfChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setIfCondition(newValue);
    updateNodeData({ ifCondition: newValue });
  };
  
  const handleThenChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setThenAction(newValue);
    updateNodeData({ thenAction: newValue });
  };
  
  const handleOtherwiseChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setOtherwiseAction(newValue);
    updateNodeData({ otherwiseAction: newValue });
  };
  
  // Helper function to update node data in the flow
  const updateNodeData = (updates: Partial<ConditionalNodeProps['data']>) => {
    setNodes((nodes) => 
      nodes.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              ...updates
            }
          };
        }
        return node;
      })
    );
  };
  
  // Auto-resize textareas based on content
  useEffect(() => {
    const adjustTextareaHeight = (elementId: string) => {
      const textarea = document.getElementById(elementId);
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
      }
    };
    
    adjustTextareaHeight(`if-condition-${id}`);
    adjustTextareaHeight(`then-action-${id}`);
    adjustTextareaHeight(`otherwise-action-${id}`);
  }, [ifCondition, thenAction, otherwiseAction, id]);

  return (
    <div className="conditional-node bg-yellow-50 shadow-md rounded-lg p-4 border-2 border-yellow-300 min-w-[200px]">
      <NodeHandle type="target" position={Position.Top} nodeId={id} />
      <div className="text-center font-medium mb-2">If / Then</div>
      
      <div className="space-y-2">
        <Textarea 
          id={`if-condition-${id}`}
          value={ifCondition} 
          onChange={handleIfChange}
          className="text-xs min-h-[60px] resize-none bg-white/70 overflow-hidden"
          placeholder="If this happens..."
          style={{ overflow: 'hidden' }}
        />
        
        <Textarea 
          id={`then-action-${id}`}
          value={thenAction} 
          onChange={handleThenChange}
          className="text-xs min-h-[60px] resize-none bg-white/70 overflow-hidden"
          placeholder="Then I do this..."
          style={{ overflow: 'hidden' }}
        />
        
        <Textarea 
          id={`otherwise-action-${id}`}
          value={otherwiseAction} 
          onChange={handleOtherwiseChange}
          className="text-xs min-h-[60px] resize-none bg-white/70 overflow-hidden"
          placeholder="Otherwise..."
          style={{ overflow: 'hidden' }}
        />
      </div>
      
      <NodeHandle type="source" position={Position.Bottom} nodeId={id} />
    </div>
  );
});
